// Pregnancy prescribing safety — deterministic screen of every Rx row.
// Used when the visit context indicates pregnancy / gestational diabetes.

export function detectPregnancy({ known = [], diagnosisPrimary = "", diagnosisSecondary = "", allergyFlags = [] }) {
  const src = `${known.join(" ")} ${diagnosisPrimary} ${diagnosisSecondary}`.toLowerCase();
  return /pregnan|gestational|\bgdm\b|antenatal/.test(src) || allergyFlags.some((f) => /pregnan/i.test(f));
}

const medText = (m) => `${m.brand_name || ""} ${m.generic_name || ""} ${m.composition || ""}`.toLowerCase();

// [regex, level, label, alternative]
const RULES = [
  [/\b(atorva|rosuva|simva|prava|fluva|pitava|lova)statin\b/, "critical", "Statins — teratogenic; stop in pregnancy", "resume post-delivery / post-lactation"],
  [/pril\b|\b(losartan|valsartan|telmisartan|olmesartan|irbesartan|candesartan|azilsartan)\b|sacubitril/, "critical", "ACEi/ARB/ARNI — fetal renal toxicity", "methyldopa, labetalol or nifedipine"],
  [/\bwarfarin\b/, "critical", "Warfarin — embryopathy", "LMWH (enoxaparin) under specialist care"],
  [/\bmethotrexate\b|\bmycophenolate\b|\bisotretinoin\b|\bacitretin\b|\bthalidomide\b|\bribavirin\b|\bmisoprostol\b/, "critical", "Absolute teratogen — contraindicated", "specialist review immediately"],
  [/\b(finasteride|dutasteride)\b/, "critical", "5-ARI — teratogenic to male fetus", "—"],
  [/\bvalproate\b|\bvalproic\b|carbamazepin|\bphenytoin\b|\btopiramate\b/, "critical", "Antiepileptic — major malformation risk", "levetiracetam / lamotrigine (specialist)"],
  [/\blithium\b/, "major", "Lithium — Ebstein anomaly risk (1st trimester)", "psychiatrist review"],
  [/gliflozin\b|\bsotagliflozin\b/, "critical", "SGLT2i — contraindicated in pregnancy", "insulin (first-line in GDM)"],
  [/\b(glimepiride|gliclazide|glipizide|glibenclamide|glyburide)\b/, "critical", "Sulfonylurea — neonatal hypoglycaemia", "insulin (first-line in GDM)"],
  [/\bpioglitazone\b|\brosiglitazone\b/, "critical", "Glitazone — contraindicated in pregnancy", "insulin"],
  [/gliptin\b/, "major", "DPP-4i — no human safety data", "insulin"],
  [/\b(sema|lira|dula)glut|tirzepatide|exenatide/, "critical", "GLP-1 — contraindicated in pregnancy", "insulin"],
  [/\bmetformin\b/, "major", "Metformin — insulin is first-line in GDM (ADA 2026 §15); metformin only under specialist obstetric care", "insulin"],
  [/\b(doxycycline|tetracycline|minocycline)\b/, "critical", "Tetracycline — fetal teeth/bone toxicity", "amoxicillin / azithromycin / cephalosporin"],
  [/floxacin\b/, "major", "Fluoroquinolone — cartilage toxicity risk; avoid", "amoxicillin / azithromycin / cephalosporin"],
  [/\b(gentamicin|amikacin|tobramycin|streptomycin)\b/, "major", "Aminoglycoside — fetal ototoxicity", "cephalosporin (per indication)"],
  [/\bcotrimoxazole\b|\btrimethoprim\b|\bsulfamethoxazole\b/, "major", "TMP-SMX — folate antagonist (1st trimester) / kernicterus (3rd)", "amoxicillin / cephalexin"],
  [/\bfluconazole\b|\bitraconazole\b/, "major", "Azole antifungal — teratogenic at high/repeated doses", "topical clotrimazole"],
  [/\b(ibuprofen|diclofenac|aceclofenac|naproxen|etoricoxib|ketorolac|indomethacin|mefenamic|nimesulide|piroxicam)\b/, "major", "NSAID — ductus arteriosus closure / oligohydramnios (esp. ≥20 wks)", "paracetamol"],
  [/\btramadol\b|\bcodeine\b/, "caution", "Opioid — neonatal withdrawal with prolonged use; short course only", "paracetamol first"],
  [/\baspirin\b|\becosprin\b/, "caution", "Aspirin — low-dose (75-150mg) OK for pre-eclampsia prophylaxis; avoid analgesic doses", "paracetamol for pain"],
  [/\bspironolactone\b/, "major", "Spironolactone — anti-androgenic", "—"],
  [/\bivabradine\b|\branolazine\b|\bamiodarone\b/, "major", "Contraindicated / fetal thyroid risk — cardiology review", "—"],
  [/\balbendazole\b|\bmebendazole\b/, "major", "Anthelmintic — avoid in 1st trimester", "defer if possible"],
];

export function pregnancyMedAlerts(medicines) {
  const out = [];
  for (const m of medicines || []) {
    const t = medText(m);
    if (!t.trim()) continue;
    for (const [re, level, label, alt] of RULES) {
      if (re.test(t)) {
        const name = m.brand_name || m.generic_name || "drug";
        out.push({ level, msg: `🤰 PREGNANCY: ${name} — ${label}.${alt && alt !== "—" ? ` Safer: ${alt}.` : ""}`, rule_id: "R-PREG" });
        break;
      }
    }
  }
  return out;
}
